/**
 * Turn a pasted streaming link into an embeddable player URL.
 * Supports Spotify, Apple Music, SoundCloud and Bandcamp (EmbeddedPlayer links).
 */
export interface EmbedInfo {
  src: string;
  provider: 'spotify' | 'apple' | 'soundcloud' | 'bandcamp' | 'other';
  height: number;
}

export function toEmbed(raw?: string | null): EmbedInfo | null {
  const url = (raw || '').trim();
  if (!url) return null;
  // user pasted a full <iframe ...> snippet
  const m = url.match(/src=["']([^"']+)["']/i);
  const u = m ? m[1] : url;
  let parsed: URL;
  try {
    parsed = new URL(u.startsWith('//') ? `https:${u}` : u);
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^www\./, '');

  if (host === 'open.spotify.com') {
    const parts = parsed.pathname.split('/').filter(Boolean).filter((p) => !p.startsWith('intl-'));
    const i = parts[0] === 'embed' ? 1 : 0;
    const type = parts[i];
    const id = parts[i + 1];
    if (!type || !id) return null;
    const tall = type === 'album' || type === 'playlist' || type === 'artist';
    return { src: `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`, provider: 'spotify', height: tall ? 452 : 152 };
  }
  if (host === 'spotify.link') return null;
  if (host === 'embed.music.apple.com') return { src: parsed.href, provider: 'apple', height: 450 };
  if (host === 'music.apple.com') {
    return { src: `https://embed.music.apple.com${parsed.pathname}${parsed.search}`, provider: 'apple', height: parsed.search.includes('i=') ? 175 : 450 };
  }
  if (host === 'w.soundcloud.com') return { src: parsed.href, provider: 'soundcloud', height: 360 };
  if (host === 'soundcloud.com' || host === 'on.soundcloud.com') {
    return {
      src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(parsed.href)}&color=%23df5eb8&auto_play=false&visual=true`,
      provider: 'soundcloud',
      height: 360,
    };
  }
  if (host === 'bandcamp.com' && parsed.pathname.startsWith('/EmbeddedPlayer')) {
    return { src: parsed.href, provider: 'bandcamp', height: 470 };
  }
  return null;
}

/** Embed for a release: explicit embed URL wins, else derived from the Spotify link. */
export function releaseEmbed(r: { embed_url?: string; spotify_url?: string }): EmbedInfo | null {
  return toEmbed(r.embed_url) || toEmbed(r.spotify_url);
}
